// Store-level on/off switch for OTA updates, plus a readiness flag.
//
// When disabled for the active store, the device does not check, download, or
// apply any web bundle update. Values are fed in from StoreContext (from the
// store's `otaUpdatesEnabled` flag) and read by the OTA scheduler each tick.
//
// `ready` guards the startup race: the scheduler must NOT act until the active
// store's OTA policy (enabled + business hours) has been explicitly hydrated.
// Until then the permissive defaults (enabled + no hours = "safe to apply")
// would let a device update during store hours, or for a store with OTA off.

let enabled = true; // default on — matches the Store schema default
let ready = false; // becomes true once a real store's policy has been pushed
const readyListeners = new Set();

const notifyReady = () => {
  readyListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      // A listener failure must not break OTA policy tracking.
      // eslint-disable-next-line no-console
      console.warn('[otaSettings] readiness listener failed', err);
    }
  });
};

export const setOtaEnabled = (value) => {
  enabled = value !== false; // treat undefined/null as enabled (schema default)
};

export const isOtaEnabled = () => enabled;

// Called by StoreContext once the active store's OTA policy has been applied.
export const markOtaPolicyReady = () => {
  const wasReady = ready;
  ready = true;
  if (!wasReady) {
    notifyReady();
  }
};

// Reset when there is no active store (logout / store switch) so a fresh login
// re-gates until the next hydration.
export const resetOtaPolicy = () => {
  ready = false;
  enabled = true;
};

// True only after the active store's OTA policy has been hydrated at least once.
export const isOtaPolicyReady = () => ready;

export const subscribeOtaPolicyReady = (listener) => {
  readyListeners.add(listener);
  return () => readyListeners.delete(listener);
};

// Test-only.
export const __resetOtaEnabled = () => {
  enabled = true;
  ready = false;
  readyListeners.clear();
};
